import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Divider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import SearchConditional from './SearchConditional';
import { DataGrid } from '@material-ui/data-grid';
import { getOrderByManifestNo } from '../../../actions/driverPickupActions';
import TableTooltip from '../../Common/TableTooltip'
import { showAlertSnackbar } from '../../../reducers/uiSlice';
import moment from 'moment';
import Button from '@material-ui/core/Button';

const useStyles = makeStyles((theme) => ({
    section: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    boxStyle: {
        height: 750
    },
    downloadButtonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    downloadButton: {
        width: '10%',
    },
}));


export default function PickupListDetail(props) {
    const classes = useStyles();
    const { t } = useTranslation('driverPickup');
    const dispatch = useDispatch();

    let pickupWarehouses = useSelector(state => state.driverPickupReducer.getPickupWarehouses);

    const columns = [
        { field: 'id', flex: 1, headerName: t('order_id'), width: 140, headerAlign: 'center', align: 'center' },
        { field: 'tno', flex: 1, headerName: 'TNO', width: 200, headerAlign: 'center', align: 'center' },
        { field: 'warehouse_name', flex: 1, headerName: t('pickup_warehouse'), width: 150, sortable: false, headerAlign: 'center', align: 'center' },
        {
            field: 'is_pickedup',
            headerName: t('is_pickedup'),
            width: 150,
            flex: 1,
            headerAlign: 'center',
            valueFormatter: (params) => params.value === 1 ? t('picked_up') : t('not_picked_up'),
            align: 'center'
        },
        {
            field: 'pickup_time',
            headerName: t('pickup_time'),
            headerAlign: 'center',
            width: 200,
            flex: 1,
            valueFormatter: (params) => params.value ? moment.unix(params.value).format("MMM DD, YYYY - HH:mm:ss") : '',
            align: 'center'
        },
        {
            field: 'pickup_location',
            headerName: t('pickup_location'),
            width: 200,
            headerAlign: 'center',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <TableTooltip value={params.value} />
            )
        },
        { field: 'manifest_no', flex: 1, headerName: t('manifest_no'), width: 150, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'sub_referer', flex: 1, headerName: t('sub_batch_no'), width: 150, sortable: false, headerAlign: 'center', align: 'center' }
    ];

    const [manifestNo, setManifestNo] = useState('')
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [pageSize, setPageSize] = useState(20);
    const [orderList, setOrderList] = useState([]);

    const _handleSearch = (manifestNo) => {
        setManifestNo(manifestNo);
        setIsDataLoading(true);
        let params = {
            manifest_no: manifestNo
        }
        dispatch(getOrderByManifestNo(params))
            .then((res) => {
                setOrderList(res);
                if (res.length === 0) {
                    dispatch(showAlertSnackbar({ message: t('no_res'), type: 'warning' }))
                } else {
                    dispatch(showAlertSnackbar({ message: t('fetch_success'), type: 'success' }))
                }
            })
            .catch(() => {
                dispatch(showAlertSnackbar({ message: t('fetch_failure'), type: 'error' }))
            })
            .finally(() => {
                setIsDataLoading(false);
            })
    }

    function downloadCSV() {
        const link = document.createElement('a');
        let csv = convertArrayOfObjectsToCSV(createRows());
        if (csv === null) return;

        const filename = 'manifest_detail.csv';

        if (!csv.match(/^data:text\/csv/i)) {
            csv = 'data:text/csv;charset=utf-8,' + csv;
        }

        link.setAttribute('href', encodeURI(csv));
        link.setAttribute('download', filename);
        link.click();
    }

    function convertArrayOfObjectsToCSV(array) {
        if (array.length === 0) {
            return null;
        }

        const columnDelimiter = ',';
        const lineDelimiter = '\n';
        const keys = Object.keys(array[0]);

        let result = '';
        result += keys.join(columnDelimiter);
        result += lineDelimiter;

        array.forEach((item) => {
            let ctr = 0;
            keys.forEach((key) => {
                if (ctr > 0) result += columnDelimiter;

                result += item[key] === null || item[key] === undefined ? '' : `"${item[key]}"`;
                ctr++;
            });
            result += lineDelimiter;
        });

        return result;
    }

    function createRows() {
        return orderList.map((row) => {
            if (pickupWarehouses && pickupWarehouses.length > 0) {
                if (pickupWarehouses.find(warehouse => warehouse.id === row.pickup_warehouse)) {
                    row['warehouse_name'] = pickupWarehouses.find(warehouse => warehouse.id === row.pickup_warehouse).name;
                } else {
                    row['warehouse_name'] = row.pickup_warehouse;
                }
                const { order_id, id, ...rest } = row
                return { id: order_id, temp: id, ...rest }
            }
        })
    }

    useEffect(() => {
        if (props.open) {
            setOrderList([]);
        }
    }, [props.open])
    useEffect(() => {
        if (props.manifestDetails && props.type === 'modal') {
            setOrderList(props.manifestDetails);
        }
    }, [props.manifestDetails, props.type])

    return (
        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {
                props.type !== 'modal' &&
                <div className={classes.section}>
                    <SearchConditional
                        onSearchClick={_handleSearch}
                    />
                </div>
            }
            <div className={classes.downloadButtonContainer}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={downloadCSV}
                    className={classes.downloadButton}
                >
                    {t('download')}
                </Button>
            </div>
            <Divider />
            <div className={classes.section} style={{ flexGrow: 1 }}>
                <DataGrid
                    loading={isDataLoading}
                    alignItems="center"
                    rows={createRows() || []}
                    columns={columns}
                    style={{ height: 600 }}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[20, 50, { value: orderList.length, label: 'All' }]}
                    pagination
                />
            </div>
        </Box>
    );
}